/**
 * Growth Report (2차 핸드오프 C · 📋 Growth Report 초안) — 화면이 그리는 리포트 모양.
 *
 * report = {
 *   person: { name, email, project, completion: 'completed'|'late', facts: [[label, value]] },
 *   status: 'draft' | 'confirmed' | 'sent' | 'failed',
 *   sections: [{ n, title, desc?, blocks: [...] }]
 * }
 * 블록 종류: bars · strengths · total · paragraphs · qa · quotes · axes · actions · items · chips · stats · rubric · self
 *
 * Free 보기는 값 데이터를 아예 넘기지 않는다 (C15) — freeShape 가 섹션 번호·제목만 남기고 블록을 빈 자리표시로 바꾼다.
 * 실제 리포트는 SAMPLE_REPORT 모양 위에 서버 본문(withContent)을 덮어 그린다. 'sample' 주소만 초안 값 그대로.
 */

export const STATUS = {
  draft: { label: 'Draft ready', tone: 'warning' },
  confirmed: { label: 'Confirmed', tone: 'success' },
  sent: { label: 'Sent', tone: 'success' },
  failed: { label: 'Send failed', tone: 'failed' },
  waiting: { label: 'Waiting', tone: 'gray' },
  not_eligible: { label: 'Not eligible', tone: 'gray' },
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const dash = (v) => v ?? '—';

/** 섹션 5 · 7~16 — 서버가 워크보드 기록에서 집계한 값 (TdReportMetrics) */
const recordSections = (c, empty) => {
  const out = {};
  if (c.axes) {
    out[5] = {
      desc: 'Observed in this project vs the job-post benchmark for your job track. Counts and ratios only. Teammate scores are shown next to each axis but not used in it.',
      blocks: [
        {
          type: 'axes',
          match: { value: dash(c.axes.match), sub: c.axes.matchSub },
          strengths: c.axes.strengths,
          gaps: c.axes.gaps,
          rows: c.axes.rows,
          label: c.axes.label,
        },
      ],
    };
  }
  if (c.contribution) {
    const { approved, contributor } = c.contribution;
    out[7] = {
      blocks:
        approved.length || contributor.length
          ? [
              ...(approved.length
                ? [{ type: 'items', title: 'Approved deliverables', items: approved }]
                : []),
              ...(contributor.length
                ? [{ type: 'items', title: 'Added as a contributor', items: contributor }]
                : []),
            ]
          : empty('No approved deliverables yet.'),
    };
  }
  if (c.problems) {
    out[8] = {
      blocks: c.problems.length
        ? [{ type: 'items', items: c.problems }]
        : empty('No blockers raised.'),
    };
  }
  if (c.iterations) {
    out[9] = {
      blocks: c.iterations.length
        ? [{ type: 'items', items: c.iterations }]
        : empty('No handoffs submitted yet.'),
    };
  }
  if (c.tools) {
    out[10] = {
      blocks: [
        {
          type: 'chips',
          title: 'Detected from deliverable links',
          items: c.tools.detected.length ? c.tools.detected : ['None detected'],
        },
        {
          type: 'chips',
          title: 'Tools you listed',
          items: c.tools.listed.length ? c.tools.listed : ['None listed'],
        },
      ],
    };
  }
  if (c.speed) {
    out[11] = {
      desc: 'Always read speed next to first-time approval.',
      blocks: [
        {
          type: 'stats',
          items: [
            ['Time to accept', dash(c.speed.accept), 'Median'],
            ['Work time', dash(c.speed.work), 'Median per task'],
            ['Review wait', dash(c.speed.wait), 'Median'],
            [
              'First-time approval',
              dash(c.speed.firstTimePct),
              `${c.speed.firstTime} of ${plural(c.speed.reviewed, 'handoff')}`,
            ],
          ],
        },
      ],
    };
  }
  if (c.deadlines) {
    const d = c.deadlines;
    out[12] = {
      desc: `Tasks with a changed deadline and resubmissions after a return are excluded (${plural(d.excluded, 'task')}).`,
      blocks: [
        {
          type: 'stats',
          items: [
            ['On time', String(d.onTime), d.onTime === 1 ? 'task' : 'tasks'],
            ['Announced delay', String(d.announced), d.announced === 1 ? 'task' : 'tasks'],
            ['Simple delay', String(d.simple), d.simple === 1 ? 'task' : 'tasks'],
          ],
        },
      ],
    };
  }
  if (c.collaboration) {
    const k = c.collaboration;
    out[13] = {
      desc: 'Requested checkpoints only.',
      blocks: [
        {
          type: 'stats',
          items: [
            ['Handoffs approved first time', `${k.firstTime} of ${k.reviewed}`, ''],
            [
              'Response time',
              dash(k.responseMedian),
              k.questions ? 'Median to questions' : 'No questions to you',
            ],
            [
              'Requested check-ins',
              `${k.checkinOnTime} · ${k.checkinLate} · ${k.checkinMissed}`,
              'On time · late · missed',
            ],
          ],
        },
      ],
    };
  }
  if (c.rubric) {
    const change = c.rubric.find(([label]) => label === 'Change handling');
    // 변경 공지는 아직 워크보드 기록이 아니라 팀장 루브릭의 Change handling 으로만 보여 준다
    out[14] = {
      blocks: change
        ? [{ type: 'items', items: [[`Change handling · ${change[2]}`, change[1]]] }]
        : empty('Not scored yet by Team lead Kim.'),
    };
    out[15] = {
      blocks: c.rubric.length
        ? [{ type: 'rubric', rows: c.rubric }]
        : empty('Not scored yet by Team lead Kim.'),
    };
  }
  if (c.self) {
    out[16] = {
      desc: 'Your Career Report self-check next to the observed values in section 5. Shown because you agreed to link your self-check.',
      blocks: c.self.length
        ? [{ type: 'self', rows: c.self }]
        : empty('No self-check scores to compare.'),
    };
  }
  return out;
};

/**
 * 서버 본문(content)으로 섹션을 바꾼다 — 1~4 · 6 은 동료 평가와 AI 초안, 5 · 7~16 은 워크보드 기록 집계.
 * content = { reviewers, behaviors: [{label, value, count, na}], strengths: [{label, count}], workAgain: {yes, total},
 *             commentCount, retro: {learned, nextGoal}, kit: [{text, source}],
 *             ai: { status: 'none'|'ready'|'error', comments: {strengths, improve}, feedback: {summary, actions: [{text, tag}]} } }
 * aiState: 'loading' 이면 AI 자리에 "작성 중", 'error' 면 Retry
 */
export const withContent = (sections, content, aiState) => {
  if (!content) return sections;
  const ai = content.ai ?? {};
  const aiBlock = (ready) =>
    ai.status === 'ready' && ai.comments
      ? ready
      : [{ type: 'aiPending', state: aiState ?? (ai.status === 'error' ? 'error' : 'idle') }];
  const empty = (text) => [{ type: 'note', text }];
  const replace = {
    1: {
      desc: 'Average per item, 1 (Not at all) to 5 (Consistently). “Didn’t see this” isn’t counted.',
      blocks:
        content.reviewers === 0
          ? empty('No teammate reviews yet.')
          : [
              {
                type: 'bars',
                max: 5,
                rows: content.behaviors
                  .filter((b) => b.value != null)
                  .map((b) => ({
                    label: b.label,
                    sub: `${plural(b.count, 'reviewer')}${b.na ? ` · ${b.na} didn’t see this` : ''}`,
                    value: b.value,
                  })),
              },
              ...(content.strengths.length
                ? [
                    {
                      type: 'strengths',
                      title: 'Top strengths',
                      items: content.strengths.map((x) => [x.label, x.count]),
                    },
                  ]
                : []),
              {
                type: 'total',
                title: 'Would work with you again',
                value: `${content.workAgain.yes} of ${content.workAgain.total}`,
                note: 'Shown as a total only',
              },
            ],
    },
    2: {
      desc: `AI summary of ${plural(content.reviewers, 'teammate')}’ comments. No quotes and no names, so no comment can be traced to one person.`,
      blocks:
        content.reviewers === 0
          ? empty('No teammate comments yet.')
          : aiBlock([
              {
                type: 'paragraphs',
                items: [
                  ['Strengths', ai.comments?.strengths],
                  ['To improve', ai.comments?.improve],
                ],
              },
            ]),
    },
    3: {
      blocks:
        content.retro.learned || content.retro.nextGoal
          ? [
              {
                type: 'qa',
                items: [
                  ['What you learned', content.retro.learned || '—'],
                  ['Your next goal', content.retro.nextGoal || '—'],
                ],
              },
            ]
          : empty('Retrospective skipped.'),
    },
    4: {
      blocks: content.kit.length
        ? [{ type: 'quotes', items: content.kit.map((k) => [k.text, k.source]) }]
        : empty('No Kit items picked.'),
    },
    6: {
      blocks: aiBlock([
        { type: 'paragraphs', items: [['What the data says', ai.feedback?.summary]] },
        {
          type: 'actions',
          title: 'Next actions',
          items: (ai.feedback?.actions ?? []).map((a) => [a.text, a.tag ? `Gap · ${a.tag}` : '']),
        },
      ]),
    },
  };
  Object.assign(replace, recordSections(content, empty));
  return sections.map((s) => (replace[s.n] ? { ...s, desc: undefined, ...replace[s.n] } : s));
};

export const SAMPLE_REPORT = {
  person: {
    id: 'leo',
    name: 'Leo Tran',
    email: 'leo.tran@example.com',
    project: 'Delivery app: the last 100 m',
    program: 'Pilot #1',
    completion: 'completed',
    facts: [
      ['Role', 'Designer'],
      ['Team', 'Team Bori'],
      ['Team size', '4 people'],
      ['Dates', 'Oct 15–17, 2026'],
    ],
  },
  status: 'draft',
  sections: [
    {
      n: 1,
      title: 'Teammate review',
      desc: 'Average per item, 1 (Not at all) to 5 (Consistently). “Didn’t see this” isn’t counted.',
      blocks: [
        {
          type: 'bars',
          max: 5,
          rows: [
            { label: 'Handed off work that was ready to use', sub: '3 reviewers', value: 4.3 },
            { label: 'Flagged delays or blockers early', sub: '3 reviewers', value: 3.0 },
            { label: 'Answered questions clearly and in time', sub: '3 reviewers', value: 4.0 },
            { label: 'Took feedback and applied it', sub: '3 reviewers', value: 4.7 },
            {
              label: 'Helped teammates move forward when stuck',
              sub: '2 reviewers · 1 didn’t see this',
              value: 3.5,
            },
          ],
        },
        {
          type: 'strengths',
          title: 'Top strengths',
          items: [
            ['Strong handoffs', 2],
            ['Learns from feedback', 1],
          ],
        },
        {
          type: 'total',
          title: 'Would work with you again',
          value: '3 of 3',
          note: 'Shown as a total only',
        },
      ],
    },
    {
      n: 2,
      title: 'Teammate comments',
      desc: 'AI summary of 3 teammates’ comments. No quotes and no names, so no comment can be traced to one person.',
      blocks: [
        {
          type: 'paragraphs',
          items: [
            [
              'Strengths',
              'Teammates say your handoffs were complete and easy to build from, and that you turned feedback around quickly.',
            ],
            [
              'To improve',
              'Two teammates would like delays flagged earlier, before check-ins rather than at them.',
            ],
          ],
        },
      ],
    },
    {
      n: 3,
      title: 'Your retrospective',
      blocks: [
        {
          type: 'qa',
          items: [
            ['What you learned', 'Handoffs go faster when the receiver sees the checklist first.'],
            ['Your next goal', 'Lead a design review on my next team project.'],
          ],
        },
      ],
    },
    {
      n: 4,
      title: 'Kit items you picked',
      blocks: [
        {
          type: 'quotes',
          items: [
            ['Drop-off flow: 4 of 6 screens done, photo step next.', 'Check-in · Checkpoint 3'],
            [
              'Six screens from address to arrived, with error states.',
              'Handoff · Design drop-off flow screens',
            ],
          ],
        },
      ],
    },
    {
      n: 5,
      title: 'You vs Korean job posts',
      desc: 'Observed in this project vs the job-post benchmark for your job track. Counts and ratios only. Teammate scores are shown next to each axis but not used in it.',
      blocks: [
        {
          type: 'axes',
          match: { value: '75%', sub: 'Designer track · scored axes only' },
          strengths: 'Deadline · Teamwork',
          gaps: 'Reporting · Delivery',
          rows: [
            {
              axis: 'Deadline',
              pct: 80,
              mark: 70,
              value: '4 of 5 on time',
              benchmark: 'Benchmark 70%',
              verdict: 'Above',
              notes: [
                '1 announced delay · 0 simple delays',
                'Tasks submitted on time · changed deadlines and resubmissions excluded',
              ],
            },
            {
              axis: 'Reporting',
              pct: 86,
              mark: 95,
              value: '6 of 7 on time',
              benchmark: 'Benchmark 95%',
              verdict: 'Below',
              notes: ['Requested checkpoints checked in on time'],
              teammates: 'Teammates · Flagged delays or blockers early · 3.0 / 5',
            },
            {
              axis: 'Responsiveness',
              value: '18 min median',
              notes: ['Median response time to questions · not scored'],
              teammates: 'Teammates · Answered questions clearly and in time · 4.0 / 5',
            },
            {
              axis: 'Teamwork',
              pct: 83,
              mark: 75,
              value: '5 of 6 first time',
              benchmark: 'Benchmark 75%',
              verdict: 'Above',
              notes: ['Your handoffs approved the first time'],
              teammates:
                'Teammates · Handed off work that was ready to use + Helped teammates move forward when stuck · 3.9 / 5',
            },
            {
              axis: 'Delivery',
              pct: 70,
              value: 'Meets',
              benchmark: 'Rubric level',
              verdict: 'Meets',
              notes: ['Team lead rubric level, used as is'],
              teammates: 'Teammates · Took feedback and applied it · 4.7 / 5',
            },
          ],
          label: 'Observed in 2days · Benchmark v1 · 2026 Q4 · based on 312 Designer job posts',
        },
      ],
    },
    {
      n: 6,
      title: 'AI feedback',
      blocks: [
        {
          type: 'paragraphs',
          items: [
            [
              'What the data says',
              'You deliver work teammates can use right away and you meet deadlines. Your gaps are in telling the team early when something slips, and in tying each deliverable clearly to its criteria.',
            ],
          ],
        },
        {
          type: 'actions',
          title: 'Next actions',
          items: [
            [
              'Post a one-line status when a task slips, before the next check-in.',
              'Gap · Reporting',
            ],
            [
              'Add a short summary to each handoff that maps to the completion criteria.',
              'Gap · Delivery',
            ],
            [
              'Reply to questions within 30 minutes in your work hours, even just to say when you’ll answer.',
              'Gap · Responsiveness',
            ],
          ],
        },
      ],
    },
    {
      n: 7,
      title: 'Role and contribution',
      blocks: [
        {
          type: 'items',
          title: 'Approved deliverables',
          items: [
            ['Design drop-off flow screens', 'Approved first time · Oct 16, 14:05 KST'],
            ['Design empty and error states', 'Approved on v2 · Oct 16, 18:40 KST'],
            ['Design component specs', 'Approved first time · Oct 16, 12:30 KST'],
          ],
        },
        {
          type: 'items',
          title: 'Added as a contributor',
          items: [['Write feature spec', 'Added by Mina Park · drafted the screen list']],
        },
      ],
    },
    {
      n: 8,
      title: 'Problem solving',
      blocks: [
        {
          type: 'items',
          items: [
            [
              'Missing copy for error states',
              'Raised Oct 16, 10:20 · resolved in 1h 10m · helped by Mina Park',
            ],
            [
              'Photo step blocked by camera permission',
              'Raised Oct 16, 15:40 · resolved in 35m · helped by Aisha Rahman',
            ],
          ],
        },
      ],
    },
    {
      n: 9,
      title: 'Feedback and iteration',
      blocks: [
        {
          type: 'items',
          items: [
            [
              'Design empty and error states · 2 versions',
              'v1 rejected: Missing spec values (spacing, color, API fields) → v2 added offline and timeout states',
            ],
            ['Design drop-off flow screens · 1 version', 'Approved first time'],
            ['Design component specs · 1 version', 'Approved first time'],
          ],
        },
      ],
    },
    {
      n: 10,
      title: 'Tools',
      blocks: [
        { type: 'chips', title: 'Detected from deliverable links', items: ['Figma', 'Notion'] },
        {
          type: 'chips',
          title: 'Tools you listed',
          items: ['Figma', 'Notion', 'Miro', 'HTML/CSS'],
        },
      ],
    },
    {
      n: 11,
      title: 'Speed by stage',
      desc: 'Always read speed next to first-time approval.',
      blocks: [
        {
          type: 'stats',
          items: [
            ['Time to accept', '12 min', 'Median'],
            ['Work time', '3h 40m', 'Median per task'],
            ['Review wait', '25 min', 'Median'],
            ['First-time approval', '83%', '5 of 6 handoffs'],
          ],
        },
      ],
    },
    {
      n: 12,
      title: 'Deadlines',
      desc: 'Tasks with a changed deadline and resubmissions after a return are excluded (1 task).',
      blocks: [
        {
          type: 'stats',
          items: [
            ['On time', '4', 'tasks'],
            ['Announced delay', '1', 'task'],
            ['Simple delay', '0', 'tasks'],
          ],
        },
      ],
    },
    {
      n: 13,
      title: 'Collaboration',
      desc: 'Requested checkpoints only.',
      blocks: [
        {
          type: 'stats',
          items: [
            ['Handoffs approved first time', '5 of 6', ''],
            ['Response time', '18 min', 'Median to questions'],
            ['Requested check-ins', '6 · 1 · 0', 'On time · late · missed'],
          ],
        },
      ],
    },
    {
      n: 14,
      title: 'Change response',
      blocks: [
        {
          type: 'items',
          items: [
            [
              'Change notice · live tracking map cut (Oct 16, 13:00 KST)',
              'Scope change accepted in 20 min · 0 reassignments · 1 suggestion credited: “Static map with ETA instead”',
            ],
          ],
        },
      ],
    },
    {
      n: 15,
      title: 'Team lead rubric',
      blocks: [
        {
          type: 'rubric',
          rows: [
            [
              'Completion criteria',
              'Whether the deliverable meets every brief criterion',
              'Meets expectations',
            ],
            [
              'Quality',
              'How well the core scenario works end to end',
              'Exceeds expectations',
            ],
            [
              'Change handling',
              'How the mid-project change notice is reflected',
              'Meets expectations',
            ],
          ],
        },
      ],
    },
    {
      n: 16,
      title: 'Self vs verified',
      desc: 'Your Career Report self-check next to the observed values in section 5. Shown because you agreed to link your self-check.',
      // 자가진단 연동에 동의한 사람만 (C8)
      onlyIfSelfLinked: true,
      blocks: [
        {
          type: 'self',
          rows: [
            {
              axis: 'Deadline',
              pct: 80,
              mark: 70,
              value: '4 of 5 on time',
              self: 'Self 70',
              verdict: 'You underrate this',
              notes: [
                '1 announced delay · 0 simple delays',
                'Tasks submitted on time · changed deadlines and resubmissions excluded',
              ],
            },
            {
              axis: 'Reporting',
              pct: 86,
              mark: 75,
              value: '6 of 7 on time',
              self: 'Self 75',
              verdict: 'You underrate this',
              notes: ['Requested checkpoints checked in on time'],
            },
            {
              axis: 'Responsiveness',
              mark: 72,
              value: '18 min median',
              self: 'Self 72',
              notes: ['Median response time to questions · not scored'],
            },
            {
              axis: 'Teamwork',
              pct: 83,
              mark: 60,
              value: '5 of 6 first time',
              self: 'Self 60',
              verdict: 'You underrate this',
              notes: ['Your handoffs approved the first time'],
            },
            {
              axis: 'Delivery',
              pct: 70,
              mark: 70,
              value: 'Meets',
              self: 'Self 70',
              verdict: 'Spot on',
              notes: ['Team lead rubric level, used as is'],
            },
          ],
        },
      ],
    },
  ],
  selfLinked: true,
};

/**
 * Free 보기 — 머리말과 섹션 번호·제목만 남긴다. 값·문장은 아예 빼고(화면에 내려가지 않게) 자리표시 줄 수만 넘긴다.
 * 실제 API 가 붙으면 서버가 이 모양으로 내려줘야 한다 (값 데이터 미전송).
 */
export const freeShape = (report) => ({
  ...report,
  sections: report.sections.map((s) => ({
    n: s.n,
    title: s.title,
    onlyIfSelfLinked: s.onlyIfSelfLinked,
    locked: true,
    lines: Math.min(4, Math.max(2, s.blocks.length + 1)),
  })),
});

/** 이메일 기본 문구 — Free card / Full report 에 따라 바뀐다 (C16) */
export const emailMessage = (kind, report) => {
  const first = report.person.name.split(' ')[0] || 'there';
  const head = `Hi ${first},\n\nThank you for completing 2 days project (${[report.person.program, report.person.project].filter(Boolean).join(' · ')}).\n\n`;
  const tail =
    '\n\nIf anything looks wrong, reply to this email or contact contact@ko-ready.com within 14 days.\n\nThe 2 days project team';
  return kind === 'full'
    ? `${head}Your full growth report is attached as a PDF. It brings together your teammates’ reviews, your own retrospective and Kit items, how your work compares with Korean job posts, and a few next actions to work on.\n\nThe report is based on what was recorded during the project. It’s a reference for your growth, not a certification.${tail}`
    : `${head}Your report card is attached. It shows your completion and the sections in your growth report. Scores, teammate comments and next actions are in the full report, which you can unlock from your account.${tail}`;
};
